/**
 * IBLManager — Standalone state for Image-Based Lighting.
 *
 * Manages the HDR file used for `scene.environment` (reflections / indirect
 * light) and the `scene.environmentIntensity` exposure multiplier.
 *
 * Decoupled from EnvironmentManager — each system stands alone.
 * The only orchestration layer that sets IBL + env + sunlight + shadow
 * together is the PresetManager.
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 * @see docs/ims-engine-api/01-architecture.md
 */

// ---------------------------------------------------------------------------
// IBLState — consumed by EnvironmentComponent for HDR loading
// ---------------------------------------------------------------------------

/** Full IBL state snapshot. */
export interface IBLState {
  /** HDR filename (e.g. 'studio.hdr', 'sunset.hdr'). */
  hdrFile: string;
  /**
   * When set, this URL is loaded directly instead of building from
   * `hdrFile` + hdriBasePath.  Use for custom / user-uploaded HDRs.
   */
  hdrUrl: string | null;
  /** scene.environmentIntensity (default: 1.0). */
  intensity: number;
  /** When true the loaded HDR envMap is also shown as scene.background. */
  backgroundVisible: boolean;
  /** Background blur amount (0 = sharp, 1 = fully blurred). Default 0.5. */
  backgroundBlur: number;
  /**
   * Y-axis rotation of the HDR environment in radians.
   * Rotates both scene.environmentRotation (reflections on car) and
   * scene.backgroundRotation (visible blurred background) together.
   * @default 0
   */
  envRotationY: number;
  /**
   * scene.backgroundIntensity — scales the visible background brightness
   * INDEPENDENTLY of scene.environmentIntensity (which drives car reflections).
   * Use to darken/brighten the blurred HDR background without affecting lighting.
   * @default 1.0
   */
  backgroundIntensity: number;
}

/** Partial config for `setIBL()` — omitted fields are unchanged. */
export interface IBLConfig {
  /** HDR filename inside the HDRI folder (e.g. 'sunset.hdr'). */
  hdrFile?: string;
  /** Arbitrary HDR URL — overrides hdrFile when set. Pass `null` to clear. */
  hdrUrl?: string | null;
  /** scene.environmentIntensity override. */
  intensity?: number;
  /** Toggle HDR as visible background. */
  backgroundVisible?: boolean;
  /** Background blur (0–1). */
  backgroundBlur?: number;
  /**
   * Y-axis rotation of the HDR in radians.
   * Rotates reflections + blurred background together.
   */
  envRotationY?: number;
  /**
   * Background brightness multiplier (scene.backgroundIntensity).
   * Does NOT affect reflections or indirect lighting on the car.
   */
  backgroundIntensity?: number;
}

// ---------------------------------------------------------------------------
// IBLManager
// ---------------------------------------------------------------------------

export class IBLManager {
  private _hdrFile   = 'studio.hdr';
  private _hdrUrl: string | null = null;
  private _intensity = 1.0;
  private _backgroundVisible = false;
  private _backgroundBlur = 0.5;
  private _envRotationY = 0;
  private _backgroundIntensity = 1.0;

  /**
   * Called whenever the IBL state changes (hdrFile, intensity, etc.).
   * The R3F EnvironmentComponent subscribes here to trigger HDR reloads.
   */
  onChange?: (state: IBLState) => void;

  // ---------------------------------------------------------------------------
  // apply
  // ---------------------------------------------------------------------------

  /**
   * Update IBL settings. Partial — only provided fields are changed.
   */
  apply(config: IBLConfig): void {
    if (config.hdrFile             !== undefined) this._hdrFile             = config.hdrFile;
    if (config.hdrUrl              !== undefined) this._hdrUrl              = config.hdrUrl;
    if (config.intensity           !== undefined) this._intensity           = config.intensity;
    if (config.backgroundVisible   !== undefined) this._backgroundVisible   = config.backgroundVisible;
    if (config.backgroundBlur      !== undefined) this._backgroundBlur      = config.backgroundBlur;
    if (config.envRotationY        !== undefined) this._envRotationY        = config.envRotationY;
    if (config.backgroundIntensity !== undefined) this._backgroundIntensity = config.backgroundIntensity;
    this._notify();
  }

  // ---------------------------------------------------------------------------
  // Getters / state
  // ---------------------------------------------------------------------------

  getState(): IBLState {
    return {
      hdrFile:             this._hdrFile,
      hdrUrl:              this._hdrUrl,
      intensity:           this._intensity,
      backgroundVisible:   this._backgroundVisible,
      backgroundBlur:      this._backgroundBlur,
      envRotationY:        this._envRotationY,
      backgroundIntensity: this._backgroundIntensity,
    };
  }

  get hdrFile():             string        { return this._hdrFile; }
  get hdrUrl():              string | null { return this._hdrUrl; }
  get intensity():           number        { return this._intensity; }
  get backgroundVisible():   boolean       { return this._backgroundVisible; }
  get backgroundBlur():      number        { return this._backgroundBlur; }
  get envRotationY():        number        { return this._envRotationY; }
  get backgroundIntensity(): number        { return this._backgroundIntensity; }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    this.onChange = undefined;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private _notify(): void {
    this.onChange?.(this.getState());
  }
}
